import { defineStore } from 'pinia'
import { useApiBaseUrl } from '~~/layers/base/composables/useApiBaseUrl'
import type { PaperPreview } from '~~/layers/base/types/paperPreview'
import { mergeCapacitorDesignerFetchOptions } from '~~/layers/base/utils/mergeCapacitorDesignerFetchOptions'

/**
 * Paper Preview Store
 *
 * Manages paper preview image IDs fetched from the API.
 * Caches previews by SKU + partName to avoid redundant API calls
 * when the drawer is opened and closed frequently.
 */
export const usePaperPreviewStore = defineStore('paperPreview', () => {
    // Store previews directly - Pinia tracks this state
    const previews = ref<Record<string, PaperPreview[]>>({})

    // Loading states
    const loading = ref<Record<string, boolean>>({})

    // Error states
    const errors = ref<Record<string, Error | null>>({})

    // Store active promises to avoid duplicate requests
    // Not a ref since promises aren't part of the reactive store state
    const activePromises = new Map<string, Promise<PaperPreview[]>>()

    /**
     * Fetch all paper previews for a specific SKU (both Card and Envelope)
     * Results are cached by SKU to avoid redundant API calls
     * Call this once per SKU, then use getCachedPreviews to filter by partName
     *
     * @param sku - Product SKU
     * @param forceRefresh - If true, bypass cache and fetch fresh data
     * @returns Array of all paper previews (Card and Envelope)
     */
    const fetchPaperPreviews = async (
        sku: string,
        forceRefresh = false
    ): Promise<PaperPreview[]> => {
        if (!sku) {
            return []
        }

        // Return existing data if available and not forcing refresh
        if (!forceRefresh && previews.value[sku]) {
            return previews.value[sku] || []
        }

        // Check if there's an active promise for this SKU
        const existingPromise = activePromises.get(sku)
        if (existingPromise) {
            return existingPromise
        }

        // Set loading state
        loading.value[sku] = true
        errors.value[sku] = null

        // Create the fetch promise and store it immediately to prevent race conditions
        const fetchPromise = (async () => {
            try {
                const apiBaseUrl = useApiBaseUrl()
                const url = `/api/configuration_template/sku/${sku}/paper_previews`
                const response: PaperPreview[] = await $fetch(
                    url,
                    await mergeCapacitorDesignerFetchOptions({
                        ...(apiBaseUrl && { baseURL: apiBaseUrl }),
                    })
                )

                // Store previews - Pinia tracks this
                previews.value[sku] = response || []

                return response || []
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error(`Failed to fetch paper previews for ${sku}:`, error)
                const errorObj = error instanceof Error ? error : new Error(String(error))
                errors.value[sku] = errorObj
                return []
            } finally {
                loading.value[sku] = false
                // Remove promise from map once it completes
                activePromises.delete(sku)
            }
        })()

        // Store the promise immediately so concurrent calls can await the same request
        activePromises.set(sku, fetchPromise)

        return fetchPromise
    }

    /**
     * Get cached paper previews for a specific SKU and part name
     * Filters the cached previews by partName
     *
     * @param sku - Product SKU
     * @param partName - Part name (e.g., "Card", "Envelope")
     * @returns Filtered previews or empty array
     */
    const getCachedPreviews = (sku: string, partName: string): PaperPreview[] => {
        if (!sku || !partName) {
            return []
        }

        const allPreviews = previews.value[sku] || []

        // Filter by part name
        const isEnvelopePart = partName.toLowerCase().includes('envelope')
        return allPreviews.filter((p) => {
            if (isEnvelopePart) {
                return p.partName.toLowerCase().includes('envelope')
            }
            return p.partName === partName
        })
    }

    /**
     * Raw Cloudflare Images id for a paper row from `/api/configuration_template/sku/:sku/paper_previews`.
     * Same row {@link getPreviewImageUrlForPaper} builds a URL from — use when persisting catalog
     * previews (e.g. envelope Face on `preview_images`) without a second upload.
     */
    /**
     * @param templateId - Optional `configuration_template.id`; when multiple templates share the same
     *   `paperId`, `.find` by paper alone can pick another template’s preview (e.g. wrong surface image).
     */
    const getPreviewImageIdForPaper = (
        sku: string,
        partName: string,
        paperId: string,
        templateId?: string | null
    ): string | null => {
        if (!sku || !partName || !paperId) {
            return null
        }
        let list = getCachedPreviews(sku, partName)
        const tid = templateId?.trim()
        if (tid) {
            const scoped = list.filter((p) => p.templateId === tid)
            if (scoped.length > 0) {
                list = scoped
            }
        }
        const preview = list.find((p) => p.paperId === paperId)
        return preview?.imageId?.trim() || null
    }

    /**
     * Get preview image URL for a specific paper ID within a SKU and part name
     * Returns null if not found
     *
     * @param sku - Product SKU
     * @param partName - Part name
     * @param paperId - Paper ID to find
     * @returns Cloudflare image URL or null
     */
    const getPreviewImageUrlForPaper = (
        sku: string,
        partName: string,
        paperId: string,
        templateId?: string | null
    ): string | null => {
        if (!sku || !partName || !paperId) {
            return null
        }

        const id = getPreviewImageIdForPaper(sku, partName, paperId, templateId)
        if (id) {
            // Use 'public' variant which works for both landscape and portrait
            return useCloudflareImgUrl(id, 'public')
        }

        return null
    }

    /**
     * Check if previews are currently loading for a specific SKU
     *
     * @param sku - Product SKU
     * @returns True if loading
     */
    const isLoading = (sku: string): boolean => {
        if (!sku) {
            return false
        }
        return loading.value[sku] || false
    }

    /**
     * Get error state for a specific SKU
     *
     * @param sku - Product SKU
     * @returns Error or null
     */
    const getError = (sku: string): Error | null => {
        if (!sku) {
            return null
        }
        return errors.value[sku] || null
    }

    /**
     * Clear previews for a specific SKU, or clear all
     *
     * @param sku - Optional SKU to clear specific data
     */
    const clearCache = (sku?: string): void => {
        if (sku) {
            // Remove key by creating new objects without that key
            // Uses destructuring with computed property names: extract the sku key (into _) and
            // collect the rest into a new object, effectively creating an immutable copy without that key
            const { [sku]: _, ...newPreviews } = previews.value
            const { [sku]: __, ...newLoading } = loading.value
            const { [sku]: ___, ...newErrors } = errors.value
            previews.value = newPreviews
            loading.value = newLoading
            errors.value = newErrors
            // Also clear any active promise for this SKU
            activePromises.delete(sku)
        } else {
            // Clear all
            previews.value = {}
            loading.value = {}
            errors.value = {}
            activePromises.clear()
        }
    }

    return {
        // State - exposed so Pinia can track it
        previews,
        loading,
        errors,
        // Methods
        fetchPaperPreviews,
        getCachedPreviews,
        getPreviewImageIdForPaper,
        getPreviewImageUrlForPaper,
        isLoading,
        getError,
        clearCache,
    }
})
